import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import BackButton from "@/components/BackButton";

const CATS = [
  { key: "all", label: "All Programmes" },
  { key: "certificate", label: "Certificate · 6 mo" },
  { key: "diploma", label: "Diploma · 1 yr" },
  { key: "degree", label: "Degree · 2 yr" },
];

export default function Catalog({ studentMode = false }) {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("all");
  const [courses, setCourses] = useState([]);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/courses");
      setCourses(data);
      if (user?.role === "student") {
        try {
          const { data: enrol } = await api.get("/enrollments/me");
          setEnrolledIds(new Set(enrol.map((e) => e.course.id)));
        } catch (_e) {
          // ignore
        }
      }
      setLoading(false);
    })();
  }, [user]);

  const filtered = useMemo(
    () => (tab === "all" ? courses : courses.filter((c) => c.category === tab)),
    [tab, courses]
  );

  const enrol = async (course) => {
    try {
      await api.post(`/enrollments/${course.id}`);
      toast.success(`Enrolled in ${course.title}`);
      setEnrolledIds(new Set([...enrolledIds, course.id]));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrolment failed");
    }
  };

  return (
    <div className="p-6 md:p-10">
      {user && <BackButton />}
      <div className={`${user ? "mt-5 " : ""}mb-8`}>
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          /catalogue
        </div>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter">
          Course Catalogue
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Certificate, Diploma & Degree programmes — every credential is anchored on the simulated Polygon network.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="rounded-none border border-foreground bg-background p-0">
          {CATS.map((c) => (
            <TabsTrigger
              key={c.key}
              value={c.key}
              className="rounded-none border-r border-foreground/20 px-4 py-2 mono text-[10px] uppercase tracking-widest last:border-r-0 data-[state=active]:bg-foreground data-[state=active]:text-background"
              data-testid={`catalog-tab-${c.key}`}
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-72 animate-pulse border border-border bg-muted" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const enrolled = enrolledIds.has(c.id);
            return (
              <div
                key={c.id}
                className="flex flex-col border-2 border-foreground bg-background hover-lift overflow-hidden"
                data-testid={`catalog-card-${c.slug}`}
              >
                <div className="relative aspect-[16/9] overflow-hidden bg-muted">
                  {c.thumbnail && (
                    <img
                      src={c.thumbnail}
                      alt={c.title}
                      className="h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute left-2 top-2 mono bg-foreground px-2 py-0.5 text-[10px] uppercase tracking-widest text-background">
                    {c.category} · {c.duration_months} mo
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="font-display text-lg font-extrabold leading-tight tracking-tight">
                    {c.title}
                  </div>
                  <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">
                    {c.short_description}
                  </p>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Tuition
                      </div>
                      <div className="font-display text-xl font-extrabold tracking-tight">
                        ${c.price.toFixed(0)}
                      </div>
                    </div>
                    {user?.role === "student" ? (
                      enrolled ? (
                        <Link to={`/app/student/learn/${c.id}`}>
                          <Button
                            className="rounded-none bg-accent text-accent-foreground hard-shadow-sm"
                            data-testid={`continue-btn-${c.slug}`}
                          >
                            Continue →
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          className="rounded-none bg-foreground text-background hard-shadow-sm"
                          onClick={() => enrol(c)}
                          data-testid={`enrol-btn-${c.slug}`}
                        >
                          Enrol
                        </Button>
                      )
                    ) : (
                      <Button
                        variant="outline"
                        className="rounded-none border-foreground hard-shadow-sm"
                        disabled
                      >
                        View
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
